import {useState} from 'react';
import {Star} from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Field,
  FieldLabel,
  Input
} from '@kroll-resolver-crisp/ui-components-react';
import {getApiErrorMessage} from '@/api/client';
import {
  useCreateSavedView,
  useDeleteSavedView,
  useSavedViews,
  useUpdateSavedView
} from '@/api/savedViewHooks';
import {SAVED_VIEW_NAME_MAX_LENGTH} from '@/api/savedViews';
import {FilterChip} from '@/components/shared/FilterChip';
import {
  BUILT_IN_CONTENT_VIEWS,
  parseContentViewQuery,
  sameContentView,
  type BuiltInView,
  type ContentViewQuery
} from '@/lib/savedViews';
import {showToast} from '@/lib/toast';

type View = BuiltInView & {readonly builtIn: boolean};

// Content's saved views: the built-in ones every analyst has, then the caller's own. Picking one puts
// its search, flag filter, sort and Filters in place. The trigger names the view the page matches;
// once a picked view's filters are changed it reads "(edited)" and offers to update it, so a view
// is refined in place rather than re-saved under a new name.
export function SavedViewsMenu({
  current,
  onApply
}: {
  readonly current: ContentViewQuery;
  readonly onApply: (query: ContentViewQuery) => void;
}) {
  const {data: saved = []} = useSavedViews('content');
  const {mutate: create, isPending: creating} = useCreateSavedView('content');
  const {mutate: update, isPending: updating} = useUpdateSavedView('content');
  const {mutate: remove} = useDeleteSavedView('content');
  const [dialog, setDialog] = useState<'save' | 'rename' | null>(null);
  const [name, setName] = useState('');

  const mine: View[] = saved.map((view) => ({
    id: view.id,
    name: view.name,
    query: parseContentViewQuery(view.query),
    builtIn: false
  }));
  const views: View[] = [...BUILT_IN_CONTENT_VIEWS.map((v) => ({...v, builtIn: true})), ...mine];

  // The view the analyst last picked or saved, so it stays named (as edited) while they refine it.
  // Follows the page whenever it matches a view outright, e.g. after a reload or a link.
  const [pickedId, setPickedId] = useState<string | null>(null);
  const matched = views.find((view) => sameContentView(view.query, current));
  if (matched && matched.id !== pickedId) {
    setPickedId(matched.id);
  }

  const picked = views.find((view) => view.id === pickedId);
  const edited = picked !== undefined && picked !== matched;
  const pickedSaved = picked && !picked.builtIn ? picked : undefined;

  const openDialog = (mode: 'save' | 'rename') => {
    setName(mode === 'rename' ? (pickedSaved?.name ?? '') : '');
    setDialog(mode);
  };

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      return;
    }

    if (dialog === 'rename' && pickedSaved) {
      update(
        {id: pickedSaved.id, name: trimmed},
        {
          onSuccess() {
            setDialog(null);
          },
          onError(error) {
            showToast(getApiErrorMessage(error, 'Failed to rename the view.'), 'error');
          }
        }
      );
      return;
    }

    create(
      {name: trimmed, query: current},
      {
        onSuccess(view) {
          setPickedId(view.id);
          setDialog(null);
          showToast(`Saved view "${view.name}".`, 'success');
        },
        onError(error) {
          showToast(getApiErrorMessage(error, 'Failed to save the view.'), 'error');
        }
      }
    );
  };

  const renderGroup = (label: string, group: readonly View[]) =>
    group.length > 0 ? (
      <>
        <DropdownMenuLabel>{label}</DropdownMenuLabel>
        {group.map((view) => (
          <DropdownMenuRadioItem key={view.id} value={view.id}>
            <span className="truncate">{view.name}</span>
          </DropdownMenuRadioItem>
        ))}
      </>
    ) : null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <FilterChip
              icon={<Star />}
              active={picked !== undefined && picked.id !== 'builtin:all'}
              label={
                picked ? (
                  <span className="max-w-48 truncate">
                    {picked.name}
                    {edited ? ' (edited)' : ''}
                  </span>
                ) : (
                  'Views'
                )
              }
              aria-label={picked ? `View: ${picked.name}${edited ? ', edited' : ''}` : 'Views'}
            />
          }
        />
        <DropdownMenuContent align="start" className="w-64">
          <DropdownMenuRadioGroup
            value={matched?.id ?? ''}
            onValueChange={(id) => {
              const view = views.find((v) => v.id === id);
              if (view) {
                setPickedId(view.id);
                onApply(view.query);
              }
            }}>
            {renderGroup(
              'Built in',
              views.filter((v) => v.builtIn)
            )}
            {mine.length > 0 ? <DropdownMenuSeparator /> : null}
            {renderGroup(
              'My views',
              views.filter((v) => !v.builtIn)
            )}
          </DropdownMenuRadioGroup>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => {
              openDialog('save');
            }}>
            Save as new view…
          </DropdownMenuItem>
          {pickedSaved ? (
            <>
              <DropdownMenuItem
                disabled={!edited || updating}
                onClick={() => {
                  update(
                    {id: pickedSaved.id, query: current},
                    {
                      onError(error) {
                        showToast(getApiErrorMessage(error, 'Failed to update the view.'), 'error');
                      }
                    }
                  );
                }}>
                <span className="truncate">Update “{pickedSaved.name}”</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  openDialog('rename');
                }}>
                <span className="truncate">Rename “{pickedSaved.name}”…</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                variant="destructive"
                onClick={() => {
                  remove(pickedSaved.id, {
                    onSuccess() {
                      setPickedId(null);
                      showToast(`Deleted view "${pickedSaved.name}".`, 'success');
                    },
                    onError(error) {
                      showToast(getApiErrorMessage(error, 'Failed to delete the view.'), 'error');
                    }
                  });
                }}>
                <span className="truncate">Delete “{pickedSaved.name}”</span>
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog
        open={dialog !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDialog(null);
          }
        }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{dialog === 'rename' ? 'Rename view' : 'Save view'}</DialogTitle>
            <DialogDescription>
              {dialog === 'rename'
                ? 'Only you see your views.'
                : 'Saves the search, flags, Filters and sort as they are now. Only you see your views.'}
            </DialogDescription>
          </DialogHeader>
          <form
            id="saved-view-form"
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}>
            <Field>
              <FieldLabel htmlFor="saved-view-name">Name</FieldLabel>
              <Input
                id="saved-view-name"
                value={name}
                maxLength={SAVED_VIEW_NAME_MAX_LENGTH}
                placeholder="e.g. Large unmonetised channels"
                onChange={(event) => {
                  setName(event.target.value);
                }}
              />
            </Field>
          </form>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDialog(null);
              }}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="saved-view-form"
              disabled={!name.trim() || creating || updating}>
              {dialog === 'rename' ? 'Rename' : 'Save view'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
